import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, BookOpen, Skull, Activity, Database, Code2,
  ExternalLink, Braces, RefreshCw, ShieldCheck, ArrowUpRight,
} from 'lucide-react';
import { MONO, SERIF, C, JsonBlock, CopyBtn } from '../components/codex/devkit';
import OnChainChip from '../components/OnChainChip';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = '/api/chancellor/analytics';

const truncate = (s) => (s ? `${s.slice(0, 4)}…${s.slice(-4)}` : '');

/* ───────────────────────────────────────────────────────────────
 * UI primitives
 * ─────────────────────────────────────────────────────────────── */
const Eyebrow = ({ children }) => (
  <p
    className="text-[10px] tracking-[0.32em] mb-2"
    style={{ fontFamily: MONO, color: C.inkSoft }}
  >{children}</p>
);

// Big, clean, minimal stat tile — number-first.
const StatTile = ({ label, value, sub, accent, Icon }) => (
  <div
    className="px-4 py-4 relative"
    style={{
      background: C.bgPanel,
      border: `1px solid ${C.border}`,
      borderTop: `2px solid ${accent}`,
    }}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-[9.5px] tracking-[0.3em]" style={{ color: C.inkSoft, fontFamily: MONO }}>{label}</span>
      <Icon className="w-3.5 h-3.5" style={{ color: accent, opacity: 0.85 }} />
    </div>
    <p
      className="text-[34px] sm:text-[40px] font-bold tabular-nums leading-none"
      style={{
        fontFamily: MONO,
        color: C.ink,
        letterSpacing: '-0.01em',
      }}
    >{value}</p>
    {sub && (
      <p className="text-[10px] mt-2 tracking-[0.2em]" style={{ color: C.inkMute, fontFamily: MONO }}>{sub}</p>
    )}
  </div>
);

// Horizontal bar row with built-in axis ticks for readability.
const BarRow = ({ label, count, max, accent }) => {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div
      className="grid grid-cols-[140px_1fr_50px] sm:grid-cols-[180px_1fr_60px] gap-3 items-center py-1.5"
    >
      <span
        className="text-[12px] truncate"
        style={{ color: C.ink, fontFamily: SERIF, textTransform: 'capitalize', fontStyle: 'italic' }}
        title={label}
      >{label}</span>
      <div
        className="h-1.5 relative"
        style={{
          background: 'rgba(0,0,0,0.55)',
          border: `1px solid ${C.border}`,
        }}
      >
        <div
          className="h-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: accent,
            boxShadow: `0 0 8px ${accent}88`,
          }}
        />
      </div>
      <span
        className="text-[12px] font-bold tabular-nums text-right"
        style={{ color: C.ink, fontFamily: MONO }}
      >{count}</span>
    </div>
  );
};

const PanelCard = ({ title, subtitle, children, testId, action }) => (
  <div
    className="px-5 py-4"
    data-testid={testId}
    style={{
      background: C.bgPanel,
      border: `1px solid ${C.border}`,
    }}
  >
    <div className="flex items-baseline justify-between mb-3 gap-3">
      <p className="text-[10px] tracking-[0.32em]" style={{ fontFamily: MONO, color: C.gold }}>{title}</p>
      <div className="flex items-center gap-2 flex-wrap">
        {subtitle && (
          <span className="text-[10px] tracking-[0.2em]" style={{ color: C.inkMute, fontFamily: MONO }}>{subtitle}</span>
        )}
        {action}
      </div>
    </div>
    {children}
  </div>
);

/* ───────────────────────────────────────────────────────────────
 * Page
 * ─────────────────────────────────────────────────────────────── */
export default function CodexAnalyticsPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${API_URL}${ENDPOINT}`);
      const d = await r.json();
      setData(d);
      setErr(null);
    } catch (e) {
      setErr(String(e));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const tagMax = useMemo(() => Math.max(1, ...(data?.top_behavior_tags || []).map(t => t.count)), [data]);
  const decreeMax = useMemo(() => Math.max(1, ...(data?.decree_types || []).map(t => t.count)), [data]);
  const activityMax = useMemo(() => Math.max(1, ...(data?.activity_levels || []).map(t => t.count)), [data]);
  const riskMax = useMemo(() => Math.max(1, ...(data?.risk_distribution || []).map(t => t.count)), [data]);
  const timelineMax = useMemo(() => Math.max(1, ...(data?.activity_timeline_30d || []).map(t => t.count)), [data]);

  const fullUrl = `${API_URL}${ENDPOINT}`;
  const curl = `curl -s "${fullUrl}"`;

  return (
    <div className="min-h-screen relative" style={{ background: C.bg, color: C.ink }}>
      {/* Quiet atmospheric backdrop — analytics need legibility. */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 80% 0%, rgba(140,30,30,0.10) 0%, transparent 50%),
            radial-gradient(ellipse at 0% 100%, rgba(120,80,30,0.06) 0%, transparent 60%)
          `,
        }}
      />
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=\\"http://www.w3.org/2000/svg\\" width=\\"180\\" height=\\"180\\"><filter id=\\"n\\"><feTurbulence baseFrequency=\\"0.85\\"/></filter><rect width=\\"100%\\" height=\\"100%\\" filter=\\"url(%23n)\\"/></svg>")',
        }}
      />

      {/* Top bar */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{
          background: 'rgba(8,4,4,0.78)',
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-4">
          <Link
            to="/halls"
            data-testid="codex-analytics-back"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] hover:brightness-125 transition"
            style={{ color: C.inkSoft, fontFamily: MONO }}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            HALLS
          </Link>
          <span style={{ color: C.inkMute, fontFamily: MONO, fontSize: 11 }}>·</span>
          <span className="text-[11px] tracking-[0.22em]" style={{ color: C.gold, fontFamily: MONO }}>
            CODEX ANALYTICS
          </span>
          <div className="flex-1" />
          <Link
            to="/codex/api"
            data-testid="codex-analytics-api-link"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] hover:brightness-125 transition"
            style={{ color: C.inkSoft, fontFamily: MONO }}
          >
            <Code2 className="w-3.5 h-3.5" />
            API DOCS
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </header>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-10 pb-24">

        {/* Hero */}
        <section className="mb-10">
          <p className="text-[10px] tracking-[0.4em] mb-3" style={{ color: C.inkSoft, fontFamily: MONO }}>
            · the public ledger ·
          </p>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight"
            style={{
              fontFamily: "'Cinzel', serif",
              color: C.ink,
              letterSpacing: '0.01em',
              textShadow: '0 0 24px rgba(255,180,80,0.18)',
            }}
            data-testid="codex-analytics-title"
          >
            Every judgment, in numbers.
          </h1>
          <p
            className="max-w-2xl text-[15px] leading-relaxed"
            style={{ fontFamily: SERIF, color: C.inkSoft }}
          >
            A living dataset of behaviour, risk, and reputation. Free for traders, dashboards, and
            researchers to query. The numbers below come straight from{' '}
            <code style={{ color: C.gold, fontFamily: MONO, fontSize: '13px' }}>{ENDPOINT}</code>.
          </p>

          {/* Devnet-live chips: program + season account links */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <OnChainChip target="program" testId="codex-analytics-onchain-program" />
            <OnChainChip target="season"  testId="codex-analytics-onchain-season" />
            <OnChainChip target="mint"    testId="codex-analytics-onchain-mint" />
          </div>

          {/* Endpoint chip + actions */}
          <div
            className="flex flex-wrap items-center gap-2 mt-5 px-3 py-2"
            style={{
              background: C.bgCode,
              border: `1px solid ${C.border}`,
              borderLeft: `3px solid ${C.gold}`,
              maxWidth: 760,
            }}
          >
            <span
              className="px-2 py-0.5 text-[10px] tracking-[0.22em]"
              style={{
                fontFamily: MONO, color: C.green, background: 'rgba(20,40,20,0.7)',
                border: `1px solid ${C.green}55`,
              }}
            >GET</span>
            <code className="text-[12.5px] break-all flex-1 min-w-0" style={{ color: C.ink, fontFamily: MONO }}>
              {fullUrl}
            </code>
            <CopyBtn text={curl} label="COPY CURL" testId="codex-copy-curl" />
            <button
              type="button"
              onClick={load}
              data-testid="codex-refresh"
              className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.18em] hover:brightness-125 transition active:scale-[0.97]"
              style={{
                fontFamily: MONO, color: C.inkSoft,
                background: 'rgba(0,0,0,0.4)',
                border: `1px solid ${C.border}`,
              }}
              aria-label="Refresh analytics"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              REFRESH
            </button>
            <button
              type="button"
              onClick={() => setShowJson(v => !v)}
              data-testid="codex-toggle-json"
              className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.18em] hover:brightness-125 transition active:scale-[0.97]"
              style={{
                fontFamily: MONO,
                color: showJson ? C.gold : C.inkSoft,
                background: showJson ? 'rgba(255,216,107,0.06)' : 'rgba(0,0,0,0.4)',
                border: `1px solid ${showJson ? C.gold : C.border}`,
              }}
            >
              <Braces className="w-3 h-3" />
              {showJson ? 'HIDE' : 'VIEW'} JSON
            </button>
          </div>
        </section>

        {err && (
          <p
            data-testid="codex-error"
            className="text-center text-sm py-6"
            style={{ color: C.red, fontFamily: MONO }}
          >Error: {err}</p>
        )}
        {!data && !err && (
          <p
            data-testid="codex-loading"
            className="text-center text-[11px] py-12 tracking-[0.32em]"
            style={{ color: C.inkSoft, fontFamily: MONO }}
          >SUMMONING THE CODEX…</p>
        )}

        {data && (
          <>
            {/* Raw JSON drawer */}
            {showJson && (
              <div className="mb-8" data-testid="codex-json-drawer">
                <p
                  className="text-[10px] tracking-[0.32em] mb-2"
                  style={{ fontFamily: MONO, color: C.inkSoft }}
                >RAW RESPONSE · /api/chancellor/analytics</p>
                <JsonBlock value={data} maxHeight={420} testId="codex-json-block" />
              </div>
            )}

            {/* Top stats — clean numeric tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="codex-stats-grid">
              <StatTile label="JUDGMENTS RECORDED" value={data.total_judgments.toLocaleString('en-US')} accent={C.gold} Icon={BookOpen} />
              <StatTile label="UNIQUE SOULS"       value={data.unique_wallets.toLocaleString('en-US')}  accent={C.rose} Icon={Skull} />
              <StatTile label="CURRENT SEASON"     value={`S${data.current_season}`} sub="rolls every 30 days" accent={C.violet} Icon={Activity} />
              <StatTile label="DATASET"            value="PUBLIC" sub="free · no auth"    accent={C.green} Icon={Database} />
            </div>

            {/* Charts grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <PanelCard title="DECREE TYPES" subtitle="distribution" testId="codex-decree-chart">
                {data.decree_types.length === 0
                  ? <p className="text-[12px] py-3 text-center" style={{ color: C.inkMute, fontFamily: SERIF, fontStyle: 'italic' }}>The Codex is silent.</p>
                  : data.decree_types.map(d => <BarRow key={d.decree_type} label={d.decree_type} count={d.count} max={decreeMax} accent={C.gold} />)}
              </PanelCard>

              <PanelCard title="BEHAVIOUR TAGS" subtitle="top 10" testId="codex-tag-chart">
                {data.top_behavior_tags.slice(0, 10).map(t => <BarRow key={t.tag} label={t.tag} count={t.count} max={tagMax} accent={C.rose} />)}
              </PanelCard>

              <PanelCard title="ACTIVITY LEVELS" testId="codex-activity-chart">
                {data.activity_levels.map(a => <BarRow key={a.activity_level} label={a.activity_level} count={a.count} max={activityMax} accent={C.violet} />)}
              </PanelCard>

              <PanelCard title="RISK DISTRIBUTION" subtitle="0-100" testId="codex-risk-chart">
                {data.risk_distribution.map(r => <BarRow key={r.range} label={r.range} count={r.count} max={riskMax} accent="#ff8c2a" />)}
              </PanelCard>
            </div>

            {/* Timeline */}
            <div className="mb-4">
              <PanelCard
                title="JUDGMENTS · LAST 30 DAYS"
                subtitle={`peak ${timelineMax}/day · color = season`}
                testId="codex-timeline"
              >
                {data.activity_timeline_30d.length === 0 ? (
                  <p className="text-[12px] py-3 text-center" style={{ color: C.inkMute, fontFamily: SERIF, fontStyle: 'italic' }}>No timeline data yet.</p>
                ) : (
                  <>
                    <div className="flex items-end gap-1 h-32" data-testid="timeline-bars">
                      {data.activity_timeline_30d.map((d, i) => {
                        const h = (d.count / timelineMax) * 100;
                        const seasonAccent = d.season_number === 3 ? C.gold : d.season_number === 2 ? C.rose : C.violet;
                        return (
                          <div
                            key={i}
                            className="flex-1 flex flex-col items-center justify-end"
                            title={`${d.day} · S${d.season_number} · ${d.count} judgment${d.count === 1 ? '' : 's'}`}
                          >
                            <div
                              className="w-full transition-all duration-500"
                              style={{
                                height: `${Math.max(h, 1)}%`,
                                background: seasonAccent,
                                boxShadow: `0 0 6px ${seasonAccent}88`,
                                minHeight: 2,
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    {/* Axis labels */}
                    <div className="flex items-center justify-between mt-2 text-[9px] tracking-[0.2em]" style={{ color: C.inkMute, fontFamily: MONO }}>
                      <span>30 days ago</span>
                      <span>today</span>
                    </div>
                  </>
                )}
              </PanelCard>
            </div>

            {/* Most judged + Verification side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8" data-testid="codex-most-judged">
              <PanelCard title={`MOST JUDGED · SEASON ${data.current_season}`} subtitle="top 10">
                {data.most_judged_this_season.length === 0
                  ? <p className="text-[12px] py-3 text-center" style={{ color: C.inkMute, fontFamily: SERIF, fontStyle: 'italic' }}>No judgments yet this season.</p>
                  : data.most_judged_this_season.map((w, i) => (
                      <Link
                        key={w.wallet}
                        to={`/soul/${w.wallet}`}
                        className="grid grid-cols-[24px_1fr_auto_auto] items-center gap-3 px-1 py-1.5 hover:bg-white/[0.04] transition-colors group"
                      >
                        <span className="text-[11px] font-bold tabular-nums" style={{ color: C.gold, fontFamily: MONO }}>#{i + 1}</span>
                        <span className="text-[12px] truncate" style={{ color: C.ink, fontFamily: MONO }}>{truncate(w.wallet)}</span>
                        <span className="text-[10px] tracking-[0.18em]" style={{ color: C.inkSoft, fontFamily: MONO }}>{w.judgments_count} reads</span>
                        <span
                          className="text-[12px] font-bold tabular-nums inline-flex items-center gap-1"
                          style={{ color: C.gold, fontFamily: MONO }}
                        >
                          +{w.reputation}
                          <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </span>
                      </Link>
                    ))}
              </PanelCard>

              <PanelCard title="VERIFICATION" subtitle="every judgment is provable">
                <div className="space-y-3 text-[12.5px]" style={{ color: C.inkSoft, fontFamily: SERIF, lineHeight: 1.55 }}>
                  <p>
                    Each judgment is hashed and written on chain — proving the
                    Chancellor authored the verdict and that nobody tampered with it after.
                    Records issued in Live Mode carry a real{' '}
                    <code style={{ color: C.gold, fontFamily: MONO, fontSize: '11.5px' }}>tx_signature</code>{' '}
                    that links directly to Solana Explorer.
                  </p>
                  <p>
                    Records issued in Demo Mode (or before any wallet has connected in Live Mode) are
                    held in the Codex without an on-chain signature. The provable status of each judgment
                    is exposed via{' '}
                    <code style={{ color: C.gold, fontFamily: MONO, fontSize: '11.5px' }}>tx_signature_is_mock</code>{' '}
                    in the API.
                  </p>
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    <OnChainChip target="program" testId="codex-analytics-verify-program" />
                    <OnChainChip target="season"  testId="codex-analytics-verify-season" />
                  </div>
                </div>
              </PanelCard>
            </div>

            {/* CTA: build on top of this */}
            <div
              className="px-5 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6"
              style={{
                background: C.bgPanel,
                border: `1px solid ${C.border}`,
                borderLeft: `3px solid ${C.gold}`,
              }}
              data-testid="codex-cta-build"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.32em] mb-1" style={{ fontFamily: MONO, color: C.gold }}>BUILD ON TOP</p>
                <p className="text-[14px]" style={{ fontFamily: SERIF, color: C.ink, fontStyle: 'italic' }}>
                  Want this in your dashboard? Every chart on this page comes from a single public endpoint.
                </p>
              </div>
              <Link
                to="/codex/api"
                className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] hover:brightness-125 transition active:scale-[0.97]"
                style={{
                  fontFamily: MONO, color: C.gold,
                  background: 'linear-gradient(180deg, rgba(40,24,8,0.92) 0%, rgba(20,12,4,1) 100%)',
                  border: `1px solid ${C.borderStrong}`,
                  flexShrink: 0,
                }}
                data-testid="codex-cta-api-link"
              >
                <Code2 className="w-3.5 h-3.5" />
                READ THE API DOCS
              </Link>
            </div>

            {/* Footer */}
            <p
              className="mt-12 text-[10px] text-center tracking-[0.3em]"
              style={{ color: C.inkMute, fontFamily: MONO }}
            >
              ◆ THE CODEX SPEAKS · DATASET GROWS WITH EVERY SCAN ◆
            </p>
          </>
        )}
      </div>
    </div>
  );
}
